import { useState, useEffect, useRef } from 'react'
import { verifyScrapeCredentials } from './verifyScrapeCredentials'
import './DataManagement.css'

const WORKER_URL = 'https://mlbb-stats-worker.waddle-dee-production.workers.dev'
// SPA routing fix applied

interface ScrapeRun {
  id: number
  run_type: string
  period: string | null
  bigrank: number | null
  target_hero_id: number | null
  status: string
  started_at: string
  completed_at: string | null
}

interface Credentials {
  username: string
  password: string
}

const PERIODS = [
  { value: 'past_7_days', label: '過去7日間' },
  { value: 'past_3_days', label: '過去3日間' },
  { value: 'past_1_days', label: '過去1日間' }
]

const BIGRANKS = [
  { value: 5, label: 'Epic' },
  { value: 6, label: 'Legend' },
  { value: 7, label: 'Mythic' },
  { value: 8, label: 'MythicalHonor' },
  { value: 9, label: 'MythicalGlory+' },
  { value: 101, label: 'ALL（全ランク）' }
]

export default function DataManagement() {
  const [credentials, setCredentials] = useState<Credentials>({ username: '', password: '' })
  const [loginError, setLoginError] = useState<string | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [activeTab, setActiveTab] = useState<'ranking' | 'detail' | 'basic' | 'thumbnail' | 'history'>('ranking')
  const [logs, setLogs] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [history, setHistory] = useState<ScrapeRun[]>([])
  const logsEndRef = useRef<HTMLDivElement>(null)
  
  // Ranking form state
  const [selectedPeriod, setSelectedPeriod] = useState('past_1_days')
  const [selectedBigranks, setSelectedBigranks] = useState<number[]>([7])
  
  // Retry state
  const [currentRunId, setCurrentRunId] = useState<number | null>(null)
  const [failedHero, setFailedHero] = useState<{heroId: number, error: string} | null>(null)

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs])

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs(prev => [...prev, `[${timestamp}] ${message}`])
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError(null)
    if (!credentials.username || !credentials.password) {
      setLoginError('ユーザー名とパスワードを入力してください')
      return
    }
    setIsLoggingIn(true)
    try {
      const result = await verifyScrapeCredentials(
        WORKER_URL,
        credentials.username,
        credentials.password
      )
      if (result.ok) {
        setIsAuthenticated(true)
        addLog('ログイン成功')
        await fetchHistory()
      } else {
        setLoginError(result.errorMessage)
      }
    } finally {
      setIsLoggingIn(false)
    }
  }

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${WORKER_URL}/api/scrape/history`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setHistory(data.data.runs)
        }
      }
    } catch (error) {
      addLog(`履歴取得エラー: ${error}`)
    }
  }

  const scrapeRanking = async (period: string, bigranks: number[]) => {
    setIsLoading(true)
    addLog(`ランキング取得開始: ${period}, ランク帯 ${bigranks.join(', ')}`)
    
    for (const bigrank of bigranks) {
      try {
        const bigrankLabel = BIGRANKS.find(b => b.value === bigrank)?.label || bigrank
        addLog(`  → ${bigrankLabel} (${bigrank}) 処理中...`)
        
        const response = await fetch(`${WORKER_URL}/api/scrape/ranking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
          },
          body: JSON.stringify({ period, bigrank })
        })
        
        const data = await response.json()
        
        if (data.success) {
          addLog(`  ✓ ${bigrankLabel} 完了: Run ID ${data.data.run_id}, ${data.data.processed.heroes_upserted}件処理 (${data.data.duration_ms}ms)`)
        } else {
          addLog(`  ✗ ${bigrankLabel} エラー: ${data.message}`)
        }
        
        // 次のランク帯前に1秒待機
        if (bigranks.indexOf(bigrank) < bigranks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      } catch (error) {
        addLog(`  ✗ ランク帯 ${bigrank} エラー: ${error}`)
      }
    }
    
    addLog(`すべてのランク帯の処理が完了しました`)
    setIsLoading(false)
    fetchHistory()
  }

  const scrapeHeroBasicInfo = async () => {
    setIsLoading(true)
    addLog('ヒーロー基本情報取得開始...')
    
    try {
      const response = await fetch(`${WORKER_URL}/api/scrape/hero-basic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        addLog(`✓ ヒーロー基本情報取得完了`)
        addLog(`  - ヒーロー数: ${data.data.heroes_processed}`)
        addLog(`  - ロール数: ${data.data.roles_saved}`)
        addLog(`  - レーン数: ${data.data.lanes_saved}`)
      } else {
        addLog(`✗ エラー: ${data.message}`)
      }
    } catch (error) {
      addLog(`✗ エラー: ${error}`)
    } finally {
      setIsLoading(false)
    }
  }

  const scrapeThumbnails = async () => {
    setIsLoading(true)
    addLog('サムネイル更新開始...')
    
    try {
      const response = await fetch(`${WORKER_URL}/api/scrape/thumbnails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        addLog(`✓ サムネイル更新完了`)
        addLog(`  - 成功: ${data.data.heroes_processed}件`)
        addLog(`  - 失敗: ${data.data.heroes_failed}件`)
        if (data.data.failed_heroes && data.data.failed_heroes.length > 0) {
          addLog(`  - 失敗したヒーロー:`)
          data.data.failed_heroes.forEach((h: {heroId: number, error: string}) => {
            addLog(`    - Hero ${h.heroId}: ${h.error}`)
          })
        }
      } else {
        addLog(`✗ エラー: ${data.message}`)
      }
    } catch (error) {
      addLog(`✗ エラー: ${error}`)
    } finally {
      setIsLoading(false)
    }
  }

  const scrapeAllHeroes = async (startFromHeroId: number = 1, existingRunId?: number) => {
    setIsLoading(true)
    setFailedHero(null)
    
    if (startFromHeroId === 1 && !existingRunId) {
      addLog('全ヒーロー詳細取得開始...')
    } else if (existingRunId) {
      addLog(`Run ID ${existingRunId} で Hero ${startFromHeroId} から再開...`)
    }
    
    let runId: number
    
    try {
      // 1. 新規run作成（最初から開始する場合）
      if (!existingRunId && startFromHeroId === 1) {
        const initResponse = await fetch(`${WORKER_URL}/api/scrape/hero-single`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
          },
          body: JSON.stringify({ hero_id: 1 })
        })
        
        const initData = await initResponse.json()
        if (!initData.success) {
          addLog(`✗ 初期化エラー: ${initData.message}`)
          setIsLoading(false)
          return
        }
        
        runId = initData.data.run_id
        setCurrentRunId(runId)
        addLog(`Run ID ${runId} で処理開始`)
      } else {
        runId = existingRunId || currentRunId!
      }
      
      // 2. ヒーローを1つずつ処理
      for (let heroId = startFromHeroId; heroId <= 132; heroId++) {
        try {
          const response = await fetch(`${WORKER_URL}/api/scrape/hero-single`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
            },
            body: JSON.stringify({ 
              run_id: runId, 
              hero_id: heroId,
              match_types: [0, 1]
            })
          })
          
          const data = await response.json()
          
          if (data.success) {
            const { hero_name, progress, processed } = data.data
            const totalSaved = processed.match_type_0.favorable_saved + 
                              processed.match_type_0.unfavorable_saved +
                              processed.match_type_1.favorable_saved + 
                              processed.match_type_1.unfavorable_saved
            addLog(`✓ ${hero_name} (${progress}) - ${totalSaved}件保存`)
          } else {
            addLog(`✗ Hero ${heroId} 失敗: ${data.message}`)
            setFailedHero({ heroId, error: data.message })
            setIsLoading(false)
            return
          }
          
          // D1書き込み負荷分散のため1秒待機
          if (heroId < 132) {
            await new Promise(resolve => setTimeout(resolve, 1000))
          }
          
        } catch (error) {
          const errorMsg = String(error)
          addLog(`✗ Hero ${heroId} エラー: ${errorMsg}`)
          setFailedHero({ heroId, error: errorMsg })
          setIsLoading(false)
          return
        }
      }
      
      addLog('すべてのヒーローの処理が完了しました')
      setCurrentRunId(null)
      setFailedHero(null)
    } catch (error) {
      addLog(`✗ エラー: ${error}`)
    } finally {
      setIsLoading(false)
      fetchHistory()
    }
  }
  
  const retryFailedHero = () => {
    if (failedHero && currentRunId) {
      addLog(`Hero ${failedHero.heroId} の再試行...`)
      scrapeAllHeroes(failedHero.heroId, currentRunId)
    }
  }
  
  const resetAndStartOver = () => {
    setFailedHero(null)
    setCurrentRunId(null)
    scrapeAllHeroes(1)
  }

  if (!isAuthenticated) {
    return (
      <div className="data-management">
        <div className="login-container">
          <h2>Data Management - Login</h2>
          <form onSubmit={handleLogin}>
            {loginError && (
              <p className="login-error" role="alert">
                {loginError}
              </p>
            )}
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => {
                  setLoginError(null)
                  setCredentials({ ...credentials, username: e.target.value })
                }}
                required
                disabled={isLoggingIn}
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => {
                  setLoginError(null)
                  setCredentials({ ...credentials, password: e.target.value })
                }}
                required
                disabled={isLoggingIn}
                autoComplete="current-password"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isLoggingIn}>
              {isLoggingIn ? '確認中…' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="data-management">
      <h1>Data Management</h1>
      
      <div className="tabs">
        <button 
          className={activeTab === 'ranking' ? 'active' : ''}
          onClick={() => setActiveTab('ranking')}
        >
          ランキング取得
        </button>
        <button
          className={activeTab === 'detail' ? 'active' : ''}
          onClick={() => setActiveTab('detail')}
        >
          詳細データ取得
        </button>
        <button
          className={activeTab === 'basic' ? 'active' : ''}
          onClick={() => setActiveTab('basic')}
        >
          ヒーロー基本情報
        </button>
        <button
          className={activeTab === 'thumbnail' ? 'active' : ''}
          onClick={() => setActiveTab('thumbnail')}
        >
          サムネイル
        </button>
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => { setActiveTab('history'); fetchHistory() }}
        >
          実行履歴
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'ranking' && (
          <div className="ranking-panel">
            <h2>ランキングデータ取得</h2>
            <div className="panel-description">
              <p>特定の期間とランク帯のランキングデータを取得します。</p>
              <p>全132ヒーローのデータを一括で取得・保存します。</p>
            </div>
            
            <div className="ranking-form">
              <div className="form-section">
                <h3>期間を選択</h3>
                <div className="radio-group">
                  {PERIODS.map(period => (
                    <label key={period.value} className="radio-label">
                      <input
                        type="radio"
                        name="period"
                        value={period.value}
                        checked={selectedPeriod === period.value}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                      />
                      {period.label}
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="form-section">
                <h3>ランク帯を選択（複数可）</h3>
                <div className="checkbox-group">
                  {BIGRANKS.map(bigrank => (
                    <label key={bigrank.value} className="checkbox-label">
                      <input
                        type="checkbox"
                        value={bigrank.value}
                        checked={selectedBigranks.includes(bigrank.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBigranks([...selectedBigranks, bigrank.value])
                          } else {
                            setSelectedBigranks(selectedBigranks.filter(v => v !== bigrank.value))
                          }
                        }}
                      />
                      {bigrank.label}
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="form-actions">
                <button 
                  onClick={() => scrapeRanking(selectedPeriod, selectedBigranks)}
                  disabled={isLoading || selectedBigranks.length === 0}
                  className="btn btn-large btn-primary"
                >
                  {isLoading ? '実行中...' : `選択した条件で取得（${selectedBigranks.length}ランク帯）`}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'detail' && (
          <div className="detail-panel">
            <h2>詳細データ取得</h2>
            <div className="panel-description">
              <p>全132ヒーローの詳細相性データを取得します。</p>
              <p>各ヒーローについて「敵チーム」「味方チーム」の両方のデータを取得します。</p>
              <p><strong>処理時間目安: 約3分（132ヒーロー × 2秒）</strong></p>
            </div>

            {failedHero ? (
              <div className="retry-panel">
                <div className="error-message">
                  <h3>⚠️ Hero {failedHero.heroId} でエラーが発生しました</h3>
                  <p>{failedHero.error}</p>
                </div>
                <div className="retry-actions">
                  <button
                    onClick={retryFailedHero}
                    disabled={isLoading}
                    className="btn btn-large btn-warning"
                  >
                    {isLoading ? '再試行中...' : `Hero ${failedHero.heroId} から再試行`}
                  </button>
                  <button
                    onClick={resetAndStartOver}
                    disabled={isLoading}
                    className="btn btn-large btn-secondary"
                  >
                    {isLoading ? '開始中...' : '最初からやり直し（新規Run）'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => scrapeAllHeroes(1)}
                disabled={isLoading}
                className="btn btn-large btn-primary"
              >
                {isLoading ? '実行中...' : '全ヒーロー詳細取得開始'}
              </button>
            )}
          </div>
        )}

        {activeTab === 'basic' && (
          <div className="basic-panel">
            <h2>ヒーロー基本情報更新</h2>
            <div className="panel-description">
              <p>ヒーローのロールとレーン情報を取得・更新します。</p>
              <p>全ヒーローの基本情報を一括で取得し、データベースに保存します。</p>
              <p><strong>処理時間目安: 約5秒</strong></p>
            </div>

            <button
              onClick={scrapeHeroBasicInfo}
              disabled={isLoading}
              className="btn btn-large btn-primary"
            >
              {isLoading ? '実行中...' : 'ヒーロー基本情報を更新'}
            </button>
          </div>
        )}

        {activeTab === 'thumbnail' && (
          <div className="thumbnail-panel">
            <h2>サムネイル更新</h2>
            <div className="panel-description">
              <p>全ヒーローのサムネイル画像を取得し、Cloudflare R2に保存します。</p>
              <p>画像は1年間キャッシュされ、高速に配信されます。</p>
              <p><strong>処理時間目安: 約15秒（132ヒーロー分）</strong></p>
            </div>

            <button
              onClick={scrapeThumbnails}
              disabled={isLoading}
              className="btn btn-large btn-primary"
            >
              {isLoading ? '実行中...' : 'サムネイルを更新'}
            </button>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-panel">
            <h2>実行履歴</h2>
            <button onClick={fetchHistory} className="btn btn-secondary" style={{marginBottom: '1rem'}}>
              更新
            </button>
            
            {history.length === 0 ? (
              <p>実行履歴がありません</p>
            ) : (
              <table className="history-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>タイプ</th>
                    <th>詳細</th>
                    <th>状態</th>
                    <th>開始時刻</th>
                    <th>完了時刻</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((run) => (
                    <tr key={run.id}>
                      <td>{run.id}</td>
                      <td>{run.run_type === 'ranking' ? 'ランキング' : '詳細'}</td>
                      <td>
                        {run.run_type === 'ranking' 
                          ? `${run.period}, ランク帯 ${run.bigrank}`
                          : '全ヒーロー'
                        }
                      </td>
                      <td className={`status-${run.status}`}>{run.status}</td>
                      <td>{new Date(run.started_at).toLocaleString()}</td>
                      <td>{run.completed_at ? new Date(run.completed_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="logs-panel">
        <h3>実行ログ</h3>
        <div className="logs-container">
          {logs.map((log, index) => (
            <div key={index} className="log-line">{log}</div>
          ))}
          <div ref={logsEndRef} />
        </div>
        <button onClick={() => setLogs([])} className="btn btn-small btn-secondary">
          クリア
        </button>
      </div>
    </div>
  )
}